import { MediaInfo } from "../../../domain/media";
import { MediaShow, Show } from "../../../domain/show";
import moment from "moment";
import { ChannelFuturePreview } from "../../../domain/channel-future-preview";

export class FuturePreviewViewModel {
  public get channelPreviewData(): ChannelFuturePreview {
    return this._channelPreviewData;
  }
  public get visible() {
    return this._visible;
  }
  public get showMediaIdField(): boolean {
    return this._selectedShow && this.selectedShow.tenantMediaUniqueId !== null;
  }
  public get windowWidth() {
    return this._windowWidth;
  }
  public get windowHeight() {
    return this._windowHeight;
  }
  public get windowTitle(): string {
    return this._windowTitle;
  }
  public get selectedShow(): ShowPreviewViewModel {
    return this._selectedShow;
  }
  public get currentChannelTime(): moment.Moment {
    return this._currentChannelTime;
  }
  public get allPreviewDurations(): {
    durationInMin: number;
    displayName: string;
  }[] {
    return this._allPreviewDurations;
  }

  private _channelPreviewData: ChannelFuturePreview;
  private _visible: boolean;
  private _selectedShow: ShowPreviewViewModel;
  private _channelTimeZone: string;
  private readonly _allPreviewDurations: {
    durationInMin: number;
    displayName: string;
  }[];
  private _currentChannelTime: moment.Moment;
  public videoMute = true;
  public selectedPreviewDuration: {
    durationInMin: number;
    displayName: string;
  };

  public state:
    | "loading"
    | "previewSetup"
    | "previewInProcess"
    | "previewEnded"
    | "unexpectedError"
    | "limitExceeded" = "previewSetup";
  private _previewEndTimeout: any;
  private _channelTimeInterval: any;
  constructor(
    private _windowTitle: string,
    private _windowWidth: string,
    private _windowHeight: string,
    private _api: IFuturePreviewApi,
  ) {
    this._allPreviewDurations = [];
    this._allPreviewDurations.push({
      durationInMin: 2,
      displayName: "2 Minutes",
    });
    this._allPreviewDurations.push({
      durationInMin: 5,
      displayName: "5 Minutes",
    });
    this._allPreviewDurations.push({
      durationInMin: 10,
      displayName: "10 Minutes",
    });
    this._allPreviewDurations.push({
      durationInMin: 15,
      displayName: "15 Minutes",
    });

    this.selectedPreviewDuration = this._allPreviewDurations[0];
  }

  async setShow(show: Show, channelTimeZone: string) {
    this.state = "loading";

    if (show instanceof MediaShow) {
      const mediaItem = await this._api.getMediaInfo(
        (show as MediaShow).mediaId,
      );
      show.customId = mediaItem.tenantMediaId;
    }
    this.videoMute = true;
    this._channelTimeZone = channelTimeZone;
    this._selectedShow = new ShowPreviewViewModel(show);
    this.selectedPreviewDuration = this._allPreviewDurations[0];

    this.state = "previewSetup";
  }

  showWindow() {
    clearTimeout(this._previewEndTimeout);
    clearInterval(this._channelTimeInterval);
    this._visible = true;
  }

  closeWindow() {
    console.log("future-preview -> closeWindow");
    this._visible = false;
    clearTimeout(this._previewEndTimeout);
    clearInterval(this._channelTimeInterval);

    if (this.state === "previewInProcess" && this._channelPreviewData) {
      this._api.deleteFuturePreview(this._channelPreviewData.id);
    }
    this._channelPreviewData = null;
  }

  formatTime(time: moment.Moment): string {
    return time.clone().tz(this._channelTimeZone).format("HH:mm:ss");
  }

  async startPreviewing() {
    this.state = "loading";
    this._currentChannelTime = null;

    const startTime = this._selectedShow.startTime
      .clone()
      .add(this._selectedShow.startOfPreviewMs, "milliseconds");
    try {
      const webRtcConnectionData =
        await this._api.createChannelFutureWebRTCPreview(
          startTime,
          this.selectedPreviewDuration.durationInMin * 60 * 1000,
        );
      console.log("future-preview -> start waiting 2 seconds");
      //wait 2 seconds
      await this.sleep(2000);
      console.log("future-preview -> set up web rtc connection");

      this._channelPreviewData = webRtcConnectionData;
      //console.log('startPreviewing-> resutl ', this._channelPreviewData);
      this.state = "previewInProcess";
    } catch (err) {
      if (err.toString().includes("exceeded the limit")) {
        this.state = "limitExceeded";
      } else {
        this.state = "unexpectedError";
      }
    }
  }
  openFullScreen(container) {
    const videoElement = container.getElementsByTagName("video")[0];
    if (videoElement && videoElement.webkitRequestFullScreen) {
      videoElement.webkitRequestFullScreen();
    }
  }

  sleep(ms): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  setupTimers() {
    this._currentChannelTime = this._selectedShow.startTime
      .clone()
      .add(this._selectedShow.startOfPreviewMs, "milliseconds");
    this._setPreviewEndTimeout();
    this._setChannelTimeInterval();
  }

  refreshPreview() {
    console.log("refreshPreview-> stage 1");
    if (this.state === "previewInProcess") {
      //this.state = 'loading';
      console.log("refreshPreview-> stage 2");
      clearInterval(this._channelTimeInterval);
      this._currentChannelTime = null;
      if (this._channelPreviewData) {
        console.log("refreshPreview-> stage 3");
        const newStartTime = this._selectedShow.startTime
          .clone()
          .add(this._selectedShow.startOfPreviewMs, "milliseconds");
        this._api.updateFuturePreview(
          this._channelPreviewData.id,
          newStartTime,
        );
        console.log("refreshPreview-> stage 4");
        this.setupTimers();
        console.log("refreshPreview-> stage 5");
        //this.state = 'previewInProcess';
      }
    }
  }

  toggleSound() {
    this.videoMute = !this.videoMute;
  }

  getSvg() {
    if (this.videoMute) {
      return "/assets/icons/Player-soundOff.svg";
    } else {
      return "/assets/icons/sound-on.svg";
    }
  }

  private _setPreviewEndTimeout() {
    clearTimeout(this._previewEndTimeout);
    this._previewEndTimeout = setTimeout(
      () => {
        this.state = "previewEnded";
        clearInterval(this._channelTimeInterval);
      },
      this.selectedPreviewDuration.durationInMin * 60 * 1000,
    );
  }

  private _setChannelTimeInterval() {
    clearInterval(this._channelTimeInterval);
    this._channelTimeInterval = setInterval(() => {
      this._currentChannelTime.add(1, "seconds");
    }, 1000);
  }

  onVideoPlaying(event: Event) {
    console.log("onVideoPlaying", event);
    //this.setupTimers();
    this.refreshPreview();
  }
}

export interface IFuturePreviewApi {
  getMediaInfo(mediaId: number): Promise<MediaInfo>;
  createChannelFutureWebRTCPreview(
    startTimeUtc: moment.Moment,
    durationMs: number,
  ): Promise<ChannelFuturePreview>;
  deleteFuturePreview(previewId: string): Promise<void>;
  updateFuturePreview(
    previewId: string,
    newStartTimeUtc: moment.Moment,
  ): Promise<void>;
}
export class ShowPreviewViewModel {
  public get duration(): moment.Duration {
    return this._show.duration.clone();
  }
  public get name(): string {
    return this._show.name;
  }
  public get tenantMediaUniqueId(): string {
    if (this._show instanceof MediaShow) {
      return (this._show as MediaShow).customId;
    }
    return null;
  }
  public get startTime(): moment.Moment {
    return this._show.startTime.clone();
  }
  public get endTime(): moment.Moment {
    return this._show.endTime.clone();
  }

  public startOfPreviewMs = 0;

  constructor(private _show: Show) {}
}
